import { CircularProgress, type SxProps, type Theme } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTheme } from '@mui/material/styles';
import type React from 'react';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  onConfirmWithReason?: (reason: string) => void;
  title?: string;
  contentText?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  showReasonField?: boolean;
  reasonLabel?: string;
  reasonOptions?: string[];
  reason?: string;
  onReasonChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  confirmButtonVariantStyle?: SxProps<Theme>;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
}

const EMPTY_OBJECT: SxProps<Theme> = {};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onConfirmWithReason,
  title = 'Confirm Action',
  contentText,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  showReasonField = false,
  reasonLabel = '',
  reasonOptions = [],
  reason,
  onReasonChange,
  confirmButtonVariantStyle,
  isLoading = false,
  sx = {},
}) => {
  const theme = useTheme();

  const handleConfirm = () => {
    if (showReasonField && onConfirmWithReason && reason) {
      onConfirmWithReason(reason);
    } else if (onConfirm) {
      onConfirm();
    }
    // Do not call onClose() here, parent component should handle closing
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          ...sx,
        },
      }}
    >
      <DialogTitle id="confirmation-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="confirmation-dialog-description">{contentText}</DialogContentText>
        {showReasonField && reasonOptions.length > 0 && (
          <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
            {reasonLabel && <FormLabel component="legend">{reasonLabel}</FormLabel>}
            <RadioGroup
              aria-label="reason"
              name="reason-radio-group"
              value={reason || ''}
              onChange={
                onReasonChange ||
                (() => {
                  // No-op fallback
                })
              }
            >
              {reasonOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={option}
                  sx={{
                    color: theme.palette.text.primary,
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          autoFocus
          disabled={(showReasonField && !reason) || isLoading}
          sx={confirmButtonVariantStyle || EMPTY_OBJECT}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
